import { create } from "zustand";

const STORAGE_KEY = "crow_daw_project_v2";

// Instrument types and their config
export const INSTRUMENT_DEFS = {
  kick:    { label: "Kick",        color: "#9333EA", category: "drum",    isMelodic: false },
  snare:   { label: "Snare",       color: "#22C55E", category: "drum",    isMelodic: false },
  hihat:   { label: "Hi-Hat",      color: "#EAB308", category: "drum",    isMelodic: false },
  clap:    { label: "Clap",        color: "#EC4899", category: "drum",    isMelodic: false },
  // Explicit routed types — bound to a kit at the AudioEngine layer so the
  // signal can never accidentally play through another instrument family.
  bass:    { label: "Bass (808)",  color: "#F97316", category: "instrument", isMelodic: true },
  melody:  { label: "Melody",      color: "#A855F7", category: "instrument", isMelodic: true },
  synth:   { label: "Galaxy Synth", color: "#22C55E", category: "instrument", isMelodic: true },
  sampler: { label: "Sampler",     color: "#EAB308", category: "instrument", isMelodic: true, hasSample: true },
  audio:   { label: "Audio Track", color: "#06B6D4", category: "audio",   isAudio: true },
};

export const FX_PLUGINS = ["eq", "compressor", "distortion", "saturation", "limiter", "delay", "reverb", "pumper"];
export const MAX_FX_PER_TRACK = 5;

export const FX_DEFS = {
  eq:         { label: "Equalizer",  color: "#aaa", gradient: "linear-gradient(90deg,#ef4444,#f59e0b,#22c55e,#06b6d4,#3b82f6,#a855f7)" },
  compressor: { label: "Compressor", color: "#4682B4" },
  distortion: { label: "Distortion", color: "#007BFF" },
  saturation: { label: "Saturation", color: "#f97316" }, // heavy analog-style drive; v9 addition
  limiter:    { label: "Limiter",    color: "#FF4500" },
  delay:      { label: "Delay",      color: "#B7AEEA" },
  reverb:     { label: "Reverb",     color: "#D2691E" },
  // v12.0 — CROW PUMPER: rhythmic volume-ducking sidechain-emulator. No
  // actual sidechain source needed — it ducks the track on every beat
  // subdivision synced to Tone.Transport tempo.
  pumper:     { label: "Crow Pumper", color: "#F43F5E" },
  waveview:   { label: "Wave View",  color: "#10B981", gradient: "linear-gradient(90deg,#10b981,#22d3ee,#a855f7,#ec4899)" },
  // v19.13 — UPWARD CLIPPER: two-stage bus processor.
  //   1) Upward expansion → weak signals ABOVE `expandThreshold` get a
  //      volume boost (gain = 1 + amount) so 808 tails and quiet
  //      instruments pull toward a consistent level.
  //   2) Soft-clipper output stage using tanh(driveIn) / tanh(driveIn=1)
  //      so peaks are gracefully rounded off at the ceiling — dense,
  //      "flat-topped" high-gain feel without harsh digital distortion.
  clipper:    { label: "Upward Clipper", color: "#EAB308", gradient: "linear-gradient(90deg,#eab308,#f97316,#ef4444)" },
};

const defaultFxParams = (plugin) => {
  switch (plugin) {
    case "eq":         return {
      // v19.13 — Multi-band EQ with 7 mid bands (up from 3) plus dedicated
      // high-pass and low-pass "filter" bands at the extremes. HP/LP have
      // user-selectable steep slopes (24 or 48 dB/oct). Peak bands span
      // ~40 Hz to ~14 kHz so producers get fine-grained control across
      // the whole spectrum without leaving the plugin.
      bands: [
        { freq: 30,    gain: 0, q: 0.71, type: "highpass",  label: "HP",         slope: 24 },
        { freq: 80,    gain: 0, q: 0.71, type: "lowshelf",  label: "LOW SHELF"  },
        { freq: 160,   gain: 0, q: 1.0,  type: "peaking",   label: "SUB"        },
        { freq: 320,   gain: 0, q: 1.0,  type: "peaking",   label: "LOW"        },
        { freq: 640,   gain: 0, q: 1.0,  type: "peaking",   label: "LOW MID"    },
        { freq: 1250,  gain: 0, q: 1.0,  type: "peaking",   label: "MID"        },
        { freq: 2500,  gain: 0, q: 1.0,  type: "peaking",   label: "HIGH MID"   },
        { freq: 5000,  gain: 0, q: 1.0,  type: "peaking",   label: "PRESENCE"   },
        { freq: 8000,  gain: 0, q: 1.0,  type: "peaking",   label: "AIR"        },
        { freq: 12000, gain: 0, q: 0.71, type: "highshelf", label: "HIGH SHELF" },
        { freq: 18000, gain: 0, q: 0.71, type: "lowpass",   label: "LP",         slope: 24 },
      ],
    };
    case "compressor": return { threshold: -20, ratio: 4 };
    case "distortion": return { amount: 0.3, wet: 0.5 };
    case "saturation": return { drive: 0.5, character: "heavy", wet: 0.8 };
    case "limiter":    return { threshold: -1 };
    case "delay":      return { time: "8n", feedback: 0.3, wet: 0.3 };
    case "reverb":     return { decay: 2.5, wet: 0.3 };
    // v12.0 — Crow Pumper defaults: 60% ducking depth (a strong-but-not-
    // ridiculous sidechain sound), 1/4-note pulse (four-on-the-floor pump).
    case "pumper":     return { amount: 0.6, mode: "1/4" };
    case "waveview":   return {};
    // v19.13 — Upward Clipper defaults. `expandThreshold` in dB (signals
    // above this get boosted); `expandAmount` = boost factor (0..1);
    // `clipDrive` = pre-clip drive (1..4); `clipCeiling` in dB (post-
    // clip ceiling). Sensible defaults land you in "gluey warm" without
    // being audible until you push.
    case "clipper":    return { expandThreshold: -24, expandAmount: 0.5, clipDrive: 1.6, clipCeiling: -0.3 };
    default:           return {};
  }
};

const trackPalette = ["#9333EA", "#22C55E", "#EAB308", "#3B82F6", "#EC4899", "#06B6D4", "#F97316", "#A855F7", "#10B981", "#F59E0B"];

const emptyPattern = () => new Array(16).fill(false);

const makeBlankTrack = (i) => ({
  id: `t${Date.now()}_${i}_${Math.random().toString(36).slice(2,6)}`,
  name: `Track ${i + 1}`,
  color: trackPalette[i % trackPalette.length],
  type: null, // empty until user assigns
  muted: false,
  solo: false,
  volume: 0.8,
  pan: 0, // stereo pan -1 (L) .. +1 (R), Mixer-only control
  glide: 0, // portamento seconds — 0 = jumpy (default), higher = notes slide (Sampler + Synth only)
  // v9.9.10 — persisted synth voice config. GalaxySynth writes here on
  // every knob turn so the live-edited envelope + oscillator survive
  // WAV export (previously lived only in the UI component's useState
  // and got dropped by Tone.Offline).
  synthConfig: null, // { oscType: "fatsawtooth"|"sine"|..., envelope: {attack, decay, sustain, release} }
  pattern: emptyPattern(),
  notes: [], // for melodic (synth/sampler) tracks: { id, step, pitch, duration }
  sampleUrl: null,
  sampleName: "",
  samplerEnv: { attack: 0.005, decay: 0.1, sustain: 1, release: 0.2 },
  samplerPads: [
    { id: "p1", pitch: "C3",  sampleUrl: null, sampleName: "" },
    { id: "p2", pitch: "D3",  sampleUrl: null, sampleName: "" },
    { id: "p3", pitch: "E3",  sampleUrl: null, sampleName: "" },
    { id: "p4", pitch: "F3",  sampleUrl: null, sampleName: "" },
    { id: "p5", pitch: "G3",  sampleUrl: null, sampleName: "" },
    { id: "p6", pitch: "A3",  sampleUrl: null, sampleName: "" },
    { id: "p7", pitch: "B3",  sampleUrl: null, sampleName: "" },
    { id: "p8", pitch: "C4",  sampleUrl: null, sampleName: "" },
  ],
  audioClips: [], // for audio tracks: { id, bar, length, url, name, peaks, duration, fadeOut }
  fxChain: [], // [{ id, plugin, params: {} }]
});

const defaultState = {
  projectInitialized: false,
  projectName: "Untitled Project",
  projectId: null,           // v10.3 — cloud project id (set after first save)
  lastAutoSavedAt: null,     // v10.3 — ISO string of last successful auto-save
  bpm: 120,
  isPlaying: false,
  // v19.8 — Set to `true` while the AudioEngine is rebuilding samplers
  // after an Edit-in-DAW hydration. The Transport Play button reads this
  // and shows "Loading…" (disabled) until every sampler URL is decoded
  // and mapped. Without this gate, the tester heard silence because the
  // engine rebuild ran fire-and-forget behind the initial render and
  // the first Play click fired into empty samplers.
  hydratingSamples: false,
  tracks: [],
  regions: [], // { id, trackId, bar, length, name }
  selectedTrackId: null,
  selectedRegionId: null,
  selectedIds: [], // multi-select: region IDs + audio clip IDs
  currentStep: -1,
  masterVolume: 0.8,
  masterFxChain: [],
  activeTab: "sequencer",
  activePlugin: null,
  totalBars: 96, // ~3 minutes scrollable at 120 BPM (96 bars × 2s)
  snapEnabled: true,
  snapDivision: 4, // 1=bar, 4=1/4, 8=1/8, 16=1/16, 32=1/32
  toolMode: "default", // "default" | "scissor" — clipboard cursor + click-to-split
  effects: {
    eq:         { low: 0, mid: 0, high: 0 },
    compressor: { threshold: -20, ratio: 4 },
    distortion: { amount: 0, wet: 0 },
    delay:      { time: "8n", feedback: 0.2, wet: 0 },
    reverb:     { decay: 2.5, wet: 0 },
    limiter:    { threshold: -1 },
  },
};

export const useDAWStore = create((set, get) => ({
  ...defaultState,

  // Undo history — snapshots the DAW state before every mutating action so
  // Ctrl+Z / the top-bar Undo button can pop the last change. Kept as a
  // bounded LIFO of shallow-cloned `{ tracks, regions, bpm, projectName }`
  // slices; anything the AudioEngine derives from these gets rebuilt on the
  // next setupSequencer tick, so we don't need to snapshot engine state.
  _history: [],
  HISTORY_MAX: 50,

  // Push the current snapshot onto the history stack. Called by every mutating
  // action below via `_snapshot()`. If the last snapshot is identical to the
  // current state we skip the push to avoid noise from redundant setters.
  _snapshot: () => {
    const s = get();
    const snap = {
      tracks: JSON.parse(JSON.stringify(s.tracks)),
      regions: JSON.parse(JSON.stringify(s.regions)),
      bpm: s.bpm,
      projectName: s.projectName,
    };
    const prev = s._history[s._history.length - 1];
    if (prev) {
      try {
        if (JSON.stringify(prev) === JSON.stringify(snap)) return;
      } catch { /* fall through */ }
    }
    const next = [...s._history, snap];
    if (next.length > s.HISTORY_MAX) next.shift();
    set({ _history: next });
  },

  undo: () => {
    const s = get();
    const last = s._history[s._history.length - 1];
    if (!last) return false;
    set({
      _history: s._history.slice(0, -1),
      tracks: last.tracks,
      regions: last.regions,
      bpm: last.bpm,
      projectName: last.projectName,
    });
    return true;
  },

  // Full-state hydration used by the Project Save/Load system. Replaces the
  // current tracks + regions + bpm + name with a saved snapshot and clears
  // the undo history (loading a project is its own reset point).
  //
  // Silent-session fix (iter 24): the AudioEngine caches per-track synths,
  // samplers, fallback synths and outputs keyed by trackId. When a project
  // loads with brand-new track ids, those cached instances are orphaned and
  // the new tracks have NO instruments wired — Play triggers into the void.
  // We tell the engine to wipe its per-track caches so the next setupSequencer
  // rebuilds every synth/sampler for the newly-hydrated tracks, and we
  // proactively re-load any kit-sourced sampler URLs so `samplerPlayers`
  // has a fresh Tone.Sampler at the correct root pitch on first Play.
  hydrateFromProject: (project) => {
    if (!project?.state) return;
    const st = project.state || {};
    // v13.1 — PURGE any legacy "bounced" tracks that were created by the
    // now-removed Bounce feature. Older saved projects may still contain
    // audio tracks whose name ends with " (bounce)" and whose audioClips
    // point at long-dead blob: URLs; those tracks are silent noise on
    // load, so we drop them here. Regions belonging to those tracks are
    // also removed to keep the arrangement clean.
    const rawTracks = Array.isArray(st.tracks) ? st.tracks : [];
    const bouncedIds = new Set(
      rawTracks
        .filter((t) => t.type === "audio" && typeof t.name === "string" && /\(bounce\)$/i.test(t.name))
        .map((t) => t.id),
    );
    const cleanTracks = rawTracks.filter((t) => !bouncedIds.has(t.id));
    const cleanRegions = (Array.isArray(st.regions) ? st.regions : []).filter((r) => !bouncedIds.has(r.trackId));
    set({
      projectInitialized: true,
      projectName: project.name || st.projectName || "Untitled Project",
      projectId: project.id || null,
      bpm: project.bpm || st.bpm || 120,
      tracks: cleanTracks,
      regions: cleanRegions,
      selectedTrackId: cleanTracks[0]?.id || null,
      selectedRegionId: null,
      selectedIds: [],
      activePlugin: null,
      activeTab: "sequencer",
      _history: [],
      // v19.11 — Gate the Transport Play button until every sampler URL
      // (native + user-uploaded) finishes decoding. Same principle as
      // the Edit-in-DAW P0 fix (v19.8) applied to the project-reopen /
      // session-restore path. Without this a reopened project could
      // fire silent samplers on the first Play click.
      hydratingSamples: cleanTracks.length > 0,
    });
    // Rebuild the audio graph — imported dynamically to avoid a store↔engine
    // circular import at module load time.
    //
    // v19.11 — This chain now AWAITS `rebuildAllForProject` (which
    // internally awaits every `loadSampleToTrack` decode) before
    // clearing the hydratingSamples gate, guaranteeing the first Play
    // click hits fully-loaded samplers.
    import("../audio/AudioEngine").then(async ({ default: engine }) => {
      try {
        await engine.init();
        await engine.rebuildAllForProject?.(cleanTracks);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[hydrateFromProject] engine rebuild failed", e);
      } finally {
        set({ hydratingSamples: false });
      }
    });
  },

  initProject: ({ bpm, trackCount, projectName }) => {
    // v11.3 STICKY-PROJECT FIX: nuke the previous IndexedDB snapshot so
    // hitting "New Project" gives you a truly blank canvas — the next
    // refresh won't resurrect the old session on top of the new one.
    // Fire-and-forget: local persistence isn't the source of truth here.
    import("./localPersistence").then(({ clearLocalSnapshot }) => {
      clearLocalSnapshot().catch(() => {});
    });
    // Default new tracks to Audio Tracks (cyan) so the DAW opens with usable audio lanes
    const tracks = Array.from({ length: Math.max(1, trackCount) }, (_, i) => {
      const t = makeBlankTrack(i);
      return {
        ...t,
        type: "audio",
        name: `Audio ${i + 1}`,
        color: INSTRUMENT_DEFS.audio.color,
      };
    });
    set({
      projectInitialized: true,
      bpm: bpm || 120,
      projectName: projectName || "Untitled Project",
      projectId: null,        // v11.3 — fresh project ⇒ no cloud id yet
      tracks,
      regions: [],
      selectedTrackId: tracks[0]?.id || null,
      activePlugin: null,
      activeTab: "sequencer",
    });
  },

  setProjectName: (projectName) => set({ projectName }),
  setProjectId: (projectId) => set({ projectId }),
  setLastAutoSavedAt: (lastAutoSavedAt) => set({ lastAutoSavedAt }),
  setBpm: (bpm) => set({ bpm }),
  setIsPlaying: (isPlaying) => set({ isPlaying }),
  setHydratingSamples: (v) => set({ hydratingSamples: !!v }),
  setCurrentStep: (currentStep) => set({ currentStep }),
  setMasterVolume: (masterVolume) => set({ masterVolume }),
  // v19.13 — Master channel FX chain. Same shape as per-track fxChain
  // but routed AFTER every track output → masterGain → [chain] → dest.
  addMasterFx: (plugin) =>
    set((state) => {
      if ((state.masterFxChain || []).length >= MAX_FX_PER_TRACK) return {};
      const id = `mfx${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
      return { masterFxChain: [...(state.masterFxChain || []), { id, plugin, params: defaultFxParams(plugin) }] };
    }),
  removeMasterFx: (fxId) =>
    set((state) => ({ masterFxChain: (state.masterFxChain || []).filter((f) => f.id !== fxId) })),
  updateMasterFxParam: (fxId, param, value) =>
    set((state) => ({
      masterFxChain: (state.masterFxChain || []).map((f) => f.id === fxId ? { ...f, params: { ...f.params, [param]: value } } : f),
    })),
  setActiveTab: (activeTab) => set({ activeTab }),
  setActivePlugin: (activePlugin) => set({ activePlugin, activeTab: "plugin" }),
  setTotalBars: (totalBars) => set({ totalBars }),
  // v19.14 — When a track is selected, ALWAYS clear any "master:..."
  // activePlugin state so the Plugin panel snaps back to the newly-
  // selected track's own instrument + FX chain. Without this the user
  // could click a track after opening a master FX, and the panel would
  // still show the master chain — the "collision" reported in the bug.
  selectTrack: (id) => set((state) => {
    const isMasterMode = typeof state.activePlugin === "string" && state.activePlugin.startsWith("master:");
    return {
      selectedTrackId: id,
      ...(isMasterMode && id ? { activePlugin: null } : {}),
    };
  }),
  selectRegion: (id) => set({ selectedRegionId: id, selectedIds: id ? [id] : [] }),
  setSelectedIds: (ids) => set({ selectedIds: ids, selectedRegionId: ids[0] || null }),
  clearSelection: () => set({ selectedIds: [], selectedRegionId: null }),

  // Track ops
  addTrack: () => {
    get()._snapshot();
    set((state) => {
      const i = state.tracks.length;
      const t = makeBlankTrack(i);
      return { tracks: [...state.tracks, { ...t, type: "audio", name: `Audio ${i + 1}`, color: INSTRUMENT_DEFS.audio.color }] };
    });
  },
  removeTrack: (id) => {
    get()._snapshot();
    set((state) => ({
      tracks: state.tracks.filter((t) => t.id !== id),
      regions: state.regions.filter((r) => r.trackId !== id),
      selectedTrackId: state.selectedTrackId === id ? null : state.selectedTrackId,
    }));
  },
  updateTrack: (id, patch) => {
    get()._snapshot();
    set((state) => ({
      tracks: state.tracks.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    }));
  },
  // v19.10 — Drag-to-reorder tracks. `fromId` is the track being dragged;
  // `toId` is the drop target. `position` is "before" or "after" and is
  // controlled by the pointer's position relative to the target header
  // (upper half → before, lower half → after). Snapshots for undo.
  reorderTracks: (fromId, toId, position = "before") => {
    if (!fromId || !toId || fromId === toId) return;
    get()._snapshot();
    set((state) => {
      const list = state.tracks.slice();
      const fromIdx = list.findIndex((t) => t.id === fromId);
      if (fromIdx < 0) return {};
      const [moved] = list.splice(fromIdx, 1);
      let toIdx = list.findIndex((t) => t.id === toId);
      if (toIdx < 0) return { tracks: state.tracks };
      if (position === "after") toIdx += 1;
      list.splice(toIdx, 0, moved);
      return { tracks: list };
    });
  },

  // v13.1 — VOLUME AUTOMATION. Each track carries an optional
  // `automation.volume` = { enabled, nodes: [{bar, value}] } where
  // value ∈ [0,1] linear gain. When enabled, AudioEngine's automation
  // loop reads the current transport bar and applies the interpolated
  // gain to the track's output bus 60x/s. Toggling OFF releases control
  // back to the static track.volume slider.
  toggleTrackAutomation: (id) => {
    get()._snapshot();
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== id) return t;
        const cur = t.automation?.volume || { enabled: false, nodes: [] };
        return { ...t, automation: { ...(t.automation || {}), volume: { ...cur, enabled: !cur.enabled } } };
      }),
    }));
  },

  // v14.1 — VOCAL RECORDING. Any audio track can be "armed" for mic
  // capture. Multiple tracks can be armed simultaneously — all armed
  // tracks receive the same recorded clip when the user hits Record.
  toggleRecordArm: (id) => {
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== id) return t;
        if (t.type !== "audio") return t; // only audio tracks can arm
        return { ...t, recordArmed: !t.recordArmed };
      }),
    }));
  },
  isRecording: false,
  setIsRecording: (v) => set({ isRecording: !!v }),
  addAutomationNode: (trackId, bar, value) => {
    get()._snapshot();
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== trackId) return t;
        const cur = t.automation?.volume || { enabled: true, nodes: [] };
        const clean = (arr) => arr.slice().sort((a, b) => a.bar - b.bar);
        return {
          ...t,
          automation: {
            ...(t.automation || {}),
            volume: { enabled: true, nodes: clean([...(cur.nodes || []), { bar: Math.max(0, bar), value: Math.max(0, Math.min(1, value)) }]) },
          },
        };
      }),
    }));
  },
  updateAutomationNode: (trackId, nodeIdx, patch) => {
    // No snapshot per-drag-tick — the caller snapshots on mousedown.
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== trackId) return t;
        const cur = t.automation?.volume;
        if (!cur || !Array.isArray(cur.nodes)) return t;
        const nodes = cur.nodes.map((n, i) => (i === nodeIdx ? { ...n, ...patch } : n));
        // Keep nodes sorted by bar so interpolation stays monotonic.
        nodes.sort((a, b) => a.bar - b.bar);
        return { ...t, automation: { ...(t.automation || {}), volume: { ...cur, nodes } } };
      }),
    }));
  },
  removeAutomationNode: (trackId, nodeIdx) => {
    get()._snapshot();
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== trackId) return t;
        const cur = t.automation?.volume;
        if (!cur || !Array.isArray(cur.nodes)) return t;
        return {
          ...t,
          automation: { ...(t.automation || {}), volume: { ...cur, nodes: cur.nodes.filter((_, i) => i !== nodeIdx) } },
        };
      }),
    }));
  },
  // v11.3 — duplicate an entire track (instrument, FX chain, volume, pan,
  // glide, sampler assignment) AND every MIDI region on it. The clone is
  // inserted directly below the source track and given a "(copy)" suffix.
  // Region ids/note ids are regenerated so undo/redo, drag, and select
  // treat the copy as a fully independent object.
  duplicateTrack: (id) => {
    get()._snapshot();
    set((state) => {
      const idx = state.tracks.findIndex((t) => t.id === id);
      if (idx < 0) return {};
      const src = state.tracks[idx];
      const newTrackId = `t${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const clone = {
        ...src,
        id: newTrackId,
        name: `${src.name} (copy)`,
        // Deep-copy mutable arrays so edits on the clone don't leak back.
        fxChain: Array.isArray(src.fxChain) ? src.fxChain.map((fx) => ({ ...fx })) : [],
        pattern: Array.isArray(src.pattern) ? [...src.pattern] : new Array(16).fill(false),
        notes: Array.isArray(src.notes)
          ? src.notes.map((n) => ({ ...n, id: `n${Date.now()}_${Math.random().toString(36).slice(2, 5)}` }))
          : [],
        // Audio clips reference blob URLs / peaks; clone the metadata but
        // reuse the same URL so both tracks play the same buffer.
        audioClips: Array.isArray(src.audioClips)
          ? src.audioClips.map((c) => ({ ...c, id: `ac${Date.now()}_${Math.random().toString(36).slice(2, 5)}` }))
          : [],
        samplerPads: Array.isArray(src.samplerPads) ? src.samplerPads.map((p) => ({ ...p })) : [],
      };
      // Clone every region attached to the source track.
      const clonedRegions = state.regions
        .filter((r) => r.trackId === id)
        .map((r) => ({
          ...r,
          id: `r${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          trackId: newTrackId,
          pattern: [...(r.pattern || new Array(16).fill(false))],
          notes: (r.notes || []).map((n) => ({ ...n, id: `n${Date.now()}_${Math.random().toString(36).slice(2, 5)}` })),
        }));
      const newTracks = [...state.tracks];
      newTracks.splice(idx + 1, 0, clone);
      return { tracks: newTracks, regions: [...state.regions, ...clonedRegions] };
    });
  },
  // Track color — updates track + cascades to every region on that track so
  // the MIDI region bubbles and the notes inside them recolor immediately.
  setTrackColor: (id, color) => {
    get()._snapshot();
    set((state) => ({
      tracks: state.tracks.map((t) => (t.id === id ? { ...t, color } : t)),
      // Note: regions render with `track.color` looked up at paint time, so
      // no region-level mutation is strictly required — but we keep the
      // cascade for any legacy paths that stashed the color on the region.
      regions: state.regions.map((r) => (r.trackId === id ? { ...r, color } : r)),
    }));
  },
  // Assign an instrument type to a track
  assignInstrument: (trackId, instrumentType) => {
    get()._snapshot();
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== trackId) return t;
        const def = INSTRUMENT_DEFS[instrumentType];
        if (!def) return t;
        return {
          ...t,
          type: instrumentType,
          name: t.type === null ? def.label : t.name,
          color: t.type === null ? def.color : t.color,
        };
      }),
    }));
  },
  toggleMute: (trackId) =>
    set((state) => ({
      tracks: state.tracks.map((t) => (t.id === trackId ? { ...t, muted: !t.muted } : t)),
    })),
  // Per-track stereo pan. Range -1..+1. Kept in the store so it persists with
  // the project + hydrates on load; AudioEngine.setTrackPan pushes the change
  // to the live Panner node for zero-latency response.
  setTrackPan: (trackId, pan) =>
    set((state) => ({
      tracks: state.tracks.map((t) => (t.id === trackId ? { ...t, pan: Math.max(-1, Math.min(1, pan || 0)) } : t)),
    })),
  // Per-track glide (portamento) — how many seconds the pitch smoothly
  // ramps between successive notes. 0 = classic step behaviour, 0.05-0.3s
  // is a musical slide, >1s creates dramatic sweeps. Applies to Synth &
  // Sampler tracks; a no-op elsewhere.
  setTrackGlide: (trackId, glide) =>
    set((state) => ({
      tracks: state.tracks.map((t) => (t.id === trackId ? { ...t, glide: Math.max(0, Math.min(4, glide || 0)) } : t)),
    })),
  // v9.9.10 — set the persisted synth voice config on a track (oscillator
  // type + ADSR envelope). Called by GalaxySynth so producer's edits
  // survive save/load AND WAV export.
  setTrackSynthConfig: (trackId, config) =>
    set((state) => ({
      tracks: state.tracks.map((t) => (t.id === trackId ? { ...t, synthConfig: { ...(t.synthConfig || {}), ...config } } : t)),
    })),
  toggleSolo: (trackId) =>
    set((state) => ({
      tracks: state.tracks.map((t) => (t.id === trackId ? { ...t, solo: !t.solo } : t)),
    })),
  // Alias so external callers (tests, integrations) can use either name.
  setTrackSolo: (trackId, val) =>
    set((state) => ({
      tracks: state.tracks.map((t) => (t.id === trackId ? { ...t, solo: !!val } : t)),
    })),

  // Step pattern ops
  toggleStep: (trackId, step) => {
    get()._snapshot();
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== trackId) return t;
        const pattern = [...t.pattern];
        pattern[step] = !pattern[step];
        return { ...t, pattern };
      }),
    }));
  },
  clearPattern: (trackId) =>
    set((state) => ({
      tracks: state.tracks.map((t) => (t.id === trackId ? { ...t, pattern: emptyPattern() } : t)),
    })),

  // Notes ops (per track)
  addNote: (trackId, note) =>
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== trackId) return t;
        const id = `n${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
        return { ...t, notes: [...t.notes, { id, duration: 1, ...note }] };
      }),
    })),
  updateNote: (trackId, noteId, patch) =>
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== trackId) return t;
        return { ...t, notes: t.notes.map((n) => (n.id === noteId ? { ...n, ...patch } : n)) };
      }),
    })),
  removeNote: (trackId, noteId) =>
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== trackId) return t;
        return { ...t, notes: t.notes.filter((n) => n.id !== noteId) };
      }),
    })),
  clearNotes: (trackId) =>
    set((state) => ({
      tracks: state.tracks.map((t) => (t.id === trackId ? { ...t, notes: [] } : t)),
    })),

  // Sampler sample on a track. `opts` may include:
  //   - samplerRootPitch: string (e.g. "C4") — pitch the sample is rooted at
  //     for Tone.Sampler pitch-shifting.
  //   - samplerOneShot: boolean — true for drum one-shots (always trigger at
  //     rootPitch regardless of note pitch), false for pitched samples.
  //   - samplerSourceKit: "drum" | "bass" | "melody" | "upload" — provenance
  //     tag used by the UI to show where the sound came from.
  setTrackSample: (trackId, sampleUrl, sampleName, opts = {}) =>
    set((state) => ({
      tracks: state.tracks.map((t) =>
        t.id === trackId ? {
          ...t,
          sampleUrl,
          sampleName,
          samplerRootPitch: opts.samplerRootPitch ?? t.samplerRootPitch ?? "C4",
          samplerOneShot:   opts.samplerOneShot   ?? false,
          samplerSourceKit: opts.samplerSourceKit ?? "upload",
          // Clear any prior load error when we set a new sample; the loader
          // will call setTrackSamplerError() with a message if the decode
          // subsequently fails.
          samplerLoadError: null,
        } : t
      ),
    })),

  // Called by the UI after loadSampleToTrack() reports a decode failure.
  // Passing null clears the error. The trigger paths in AudioEngine watch
  // this field and fall back to a Galaxy Synth voice when it's set.
  setTrackSamplerError: (trackId, message) =>
    set((state) => ({
      tracks: state.tracks.map((t) =>
        t.id === trackId ? { ...t, samplerLoadError: message || null } : t
      ),
    })),

  // FX chain per track
  addFxToTrack: (trackId, plugin) =>
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== trackId) return t;
        if ((t.fxChain || []).length >= MAX_FX_PER_TRACK) return t;
        const id = `fx${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
        return { ...t, fxChain: [...(t.fxChain || []), { id, plugin, params: defaultFxParams(plugin) }] };
      }),
    })),
  removeFxFromTrack: (trackId, fxId) =>
    set((state) => ({
      tracks: state.tracks.map((t) =>
        t.id === trackId ? { ...t, fxChain: (t.fxChain || []).filter((f) => f.id !== fxId) } : t
      ),
    })),
  updateFxParam: (trackId, fxId, param, value) =>
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== trackId) return t;
        return {
          ...t,
          fxChain: (t.fxChain || []).map((f) =>
            f.id === fxId ? { ...f, params: { ...f.params, [param]: value } } : f
          ),
        };
      }),
    })),

  // Regions
  addRegion: (trackId, bar = 0, length = 1, name) => {
    get()._snapshot();
    let newId = null;
    set((state) => {
      const id = `r${Date.now()}_${Math.random().toString(36).slice(2,7)}`;
      newId = id;
      const track = state.tracks.find((t) => t.id === trackId);
      const region = {
        id, trackId, bar, length,
        name: name || (track?.name || "Region"),
        pattern: new Array(16).fill(false),
        notes: [],
      };
      return { regions: [...state.regions, region], selectedRegionId: id };
    });
    return newId;
  },
  updateRegion: (id, patch) => {
    get()._snapshot();
    set((state) => ({
      regions: state.regions.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  },

  // Region-scoped MIDI content
  toggleRegionStep: (regionId, step) => {
    get()._snapshot();
    set((state) => ({
      regions: state.regions.map((r) => {
        if (r.id !== regionId) return r;
        const pattern = [...(r.pattern || new Array(16).fill(false))];
        pattern[step] = !pattern[step];
        return { ...r, pattern };
      }),
    }));
  },
  clearRegionPattern: (regionId) => {
    get()._snapshot();
    set((state) => ({
      regions: state.regions.map((r) =>
        r.id === regionId ? { ...r, pattern: new Array(16).fill(false) } : r
      ),
    }));
  },
  addRegionNote: (regionId, note) => {
    get()._snapshot();
    set((state) => ({
      regions: state.regions.map((r) => {
        if (r.id !== regionId) return r;
        const id = `n${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
        return { ...r, notes: [...(r.notes || []), { id, duration: 1, ...note }] };
      }),
    }));
  },
  updateRegionNote: (regionId, noteId, patch) => {
    // Note: no snapshot per-drag-tick — the piano-roll drag handler already
    // captures a start-of-drag snapshot via `_snapshot()` on mousedown so
    // Ctrl+Z reverts the whole drag as one action instead of 60 micro-frames.
    set((state) => ({
      regions: state.regions.map((r) =>
        r.id !== regionId ? r : { ...r, notes: (r.notes || []).map((n) => (n.id === noteId ? { ...n, ...patch } : n)) }
      ),
    }));
  },
  removeRegionNote: (regionId, noteId) => {
    get()._snapshot();
    set((state) => ({
      regions: state.regions.map((r) =>
        r.id !== regionId ? r : { ...r, notes: (r.notes || []).filter((n) => n.id !== noteId) }
      ),
    }));
  },
  clearRegionNotes: (regionId) =>
    set((state) => ({
      regions: state.regions.map((r) =>
        r.id !== regionId ? r : { ...r, notes: [] }
      ),
    })),

  // Turn a drum-sequencer region into editable piano-roll notes. Each true
  // step in the 16-step pattern becomes a MIDI note at the drum's native
  // pitch (kick=C2, snare=D2, hihat=F#2, clap=E2). The track flips to
  // viewMode='pr' so the bottom panel renders the PianoRoll instead of the
  // StepSequencer, and the engine's drum trigger now reads `notes` instead
  // of `pattern` — giving 1/64-resolution edit headroom.
  convertRegionToPianoRoll: (regionId) =>
    set((state) => {
      const region = state.regions.find((r) => r.id === regionId);
      if (!region) return state;
      const track = state.tracks.find((t) => t.id === region.trackId);
      if (!track) return state;
      const NATIVE_PITCH = { kick: "C2", snare: "D2", hihat: "F#2", clap: "E2" };
      const pitch = NATIVE_PITCH[track.type];
      if (!pitch) return state;  // not a drum track; nothing to convert
      const generated = (region.pattern || []).reduce((acc, on, step) => {
        if (!on) return acc;
        acc.push({
          id: `n${Date.now()}_${step}_${Math.random().toString(36).slice(2, 6)}`,
          step,
          pitch,
          duration: 1,
        });
        return acc;
      }, []);
      // Dedupe: drop any pre-existing note that lands on the same (step,pitch)
      // so converting twice doesn't stack identical notes on the grid.
      const filtered = (region.notes || []).filter(
        (n) => !generated.some((g) => g.step === n.step && g.pitch === n.pitch),
      );
      const nextNotes = [...filtered, ...generated];
      return {
        regions: state.regions.map((r) =>
          r.id === regionId ? { ...r, notes: nextNotes } : r
        ),
        tracks: state.tracks.map((t) =>
          t.id === track.id ? { ...t, viewMode: "pr" } : t
        ),
      };
    }),

  // Drop back to the step sequencer view (notes stay on the region as data
  // but stop driving playback; the original 16-step pattern is in charge).
  resetTrackToStep: (trackId) =>
    set((state) => ({
      tracks: state.tracks.map((t) =>
        t.id === trackId ? { ...t, viewMode: "step" } : t
      ),
    })),

  // Snap settings
  setSnapEnabled: (snapEnabled) => set({ snapEnabled }),
  setSnapDivision: (snapDivision) => set({ snapDivision }),
  setToolMode: (toolMode) => set({ toolMode }),
  removeRegion: (id) =>
    set((state) => ({
      regions: state.regions.filter((r) => r.id !== id),
      selectedRegionId: state.selectedRegionId === id ? null : state.selectedRegionId,
      selectedIds: state.selectedIds.filter((x) => x !== id),
    })),

  // v12.0 — Duplicate a region with ZERO overlap on its own track. The
  // copy is placed immediately AFTER the original ends on the timeline.
  // If any other region on the same track already occupies that slot,
  // the copy is shifted right until an open span of `original.length`
  // bars is found. Clamped to the arrangement length so we never spawn
  // regions outside the visible timeline.
  duplicateRegion: (id) =>
    set((state) => {
      const r = state.regions.find((x) => x.id === id);
      if (!r) return {};
      const newId = `r${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const sameTrack = state.regions
        .filter((x) => x.trackId === r.trackId && x.id !== id)
        .sort((a, b) => a.bar - b.bar);
      // Start immediately after the source region.
      let newBar = r.bar + r.length;
      // Walk right past any region that would overlap the copy.
      for (const other of sameTrack) {
        const otherEnd = other.bar + other.length;
        if (newBar + r.length <= other.bar) break;      // fits before this region
        if (newBar < otherEnd) newBar = otherEnd;       // shove past this region
      }
      // Clamp inside the arrangement — if there's no room at the tail
      // AND no room by shoving right, we still refuse to overlap by
      // clamping to the last available slot; but if that slot itself is
      // occupied, we walk once more.
      const maxStart = Math.max(0, state.totalBars - r.length);
      if (newBar > maxStart) newBar = maxStart;
      // Final safety: if the clamped position still overlaps the source,
      // fall back to right-after-source (which is our contract).
      if (newBar < r.bar + r.length) newBar = r.bar + r.length;
      const copy = {
        ...r,
        id: newId,
        bar: newBar,
        // v19.25 — Duplicates inherit the exact source name (no "copy"
        // suffix). Prevents "Sampler copy copy copy" stacking on
        // repeated duplication. The spread above already carries r.name
        // through unchanged; this comment documents the intent so a
        // future refactor doesn't reintroduce the suffix.
        pattern: [...(r.pattern || new Array(16).fill(false))],
        notes: (r.notes || []).map((n) => ({ ...n, id: `n${Date.now()}_${Math.random().toString(36).slice(2, 5)}` })),
      };
      return { regions: [...state.regions, copy], selectedRegionId: newId };
    }),

  // v12.0 — Duplicate a note on the same track, placed AFTER the source
  // ends so both never sound at once. Search for the next open slot of
  // the same duration+pitch; if none, walk to end of the track's note
  // set. Used by the PianoRoll copy shortcut.
  duplicateNote: (trackId, noteId) =>
    set((state) => {
      const track = state.tracks.find((t) => t.id === trackId);
      if (!track) return {};
      const src = (track.notes || []).find((n) => n.id === noteId);
      if (!src) return {};
      const dur = src.duration || 1;
      // Same-pitch occupancy scan so C4-then-C4 gets pushed past every
      // other C4 on the track. Different pitches at the same time are
      // fine — that's a chord, not a doubling.
      const sameRow = (track.notes || [])
        .filter((n) => n.pitch === src.pitch && n.id !== noteId)
        .sort((a, b) => a.time - b.time);
      let newTime = src.time + dur;
      for (const other of sameRow) {
        const otherEnd = other.time + (other.duration || 1);
        if (newTime + dur <= other.time) break;
        if (newTime < otherEnd) newTime = otherEnd;
      }
      const newNote = {
        ...src,
        id: `n${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        time: newTime,
      };
      return {
        tracks: state.tracks.map((t) =>
          t.id === trackId ? { ...t, notes: [...(t.notes || []), newNote] } : t
        ),
      };
    }),

  // v12.5 — Join multiple selected regions on the SAME track into one
  // merged region. The join span runs from the earliest start to the
  // latest end of the selection. All notes are merged with their
  // absolute-time offsets preserved relative to the NEW start. If the
  // regions span multiple tracks, joining is refused (returns null) so
  // callers can toast a helpful error.
  //
  // Returns the id of the new merged region on success, null on refusal.
  joinRegions: (ids) => {
    let mergedId = null;
    set((state) => {
      // v12.6 CORRECTION — the previous impl multiplied by 4 steps/bar
      // (drum-lane math) which shifted note.step to wrong positions when
      // joining Piano-Roll regions. Notes ARE stored at 1/16 resolution
      // → 16 steps per bar. Sort picks left-to-right so the merged
      // region's start is deterministic even if selectedIds is a Set.
      const picks = state.regions
        .filter((r) => ids.includes(r.id))
        .sort((a, b) => a.bar - b.bar);
      if (picks.length < 2) return {};
      const trackId = picks[0].trackId;
      if (!picks.every((p) => p.trackId === trackId)) return {}; // multi-track join blocked
      const startBar = picks[0].bar;
      const endBar = Math.max(...picks.map((p) => p.bar + (p.length || 1)));
      const newLen = Math.max(0.25, endBar - startBar);
      // Collapse patterns (16-step drum-style regions): OR the step lanes.
      const mergedPattern = new Array(16).fill(false);
      for (const p of picks) {
        const pat = p.pattern || [];
        for (let i = 0; i < 16; i++) if (pat[i]) mergedPattern[i] = true;
      }
      // v12.6 — Absolute-step math per user spec:
      //   absoluteStep = (sourceRegion.bar - minBar) * 16 + note.step
      // A note at step 0 of a region that starts 2 bars into the join
      // lands at step 32 in the merged region. Zero drift, zero
      // overlap — Region A's notes stay put, Region B's notes stay
      // put, just now in one combined region.
      const STEPS_PER_BAR = 16;
      const mergedNotes = [];
      for (const p of picks) {
        const shiftSteps = (p.bar - startBar) * STEPS_PER_BAR;
        const shiftBars = p.bar - startBar;
        for (const n of (p.notes || [])) {
          mergedNotes.push({
            ...n,
            id: `n${Date.now()}_${Math.random().toString(36).slice(2, 5)}${mergedNotes.length}`,
            step: (n.step ?? 0) + shiftSteps,
            time: (n.time ?? 0) + shiftBars,
          });
        }
      }
      const newRegion = {
        ...picks[0],
        id: `r${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        bar: startBar,
        length: newLen,
        name: `${picks[0].name} +${picks.length - 1}`,
        pattern: mergedPattern,
        notes: mergedNotes,
      };
      mergedId = newRegion.id;
      return {
        regions: [...state.regions.filter((r) => !ids.includes(r.id)), newRegion],
        selectedRegionId: newRegion.id,
        selectedIds: [newRegion.id],
      };
    });
    return mergedId;
  },

  // v12.6 — Intra-region note duplication for Cmd+D inside the Piano Roll.
  // Copies the selected notes and places each copy at
  //   original.step + selectionSpan
  // where selectionSpan = max(step + duration) - min(step). This puts
  // the duplicates immediately adjacent to the originals with zero
  // overlap. If any copy would land on top of an existing same-pitch
  // note, that copy is nudged right to the next open slot (per-pitch
  // occupancy scan mirrors v12.0's duplicateNote invariant).
  // If the duplicates would extend past the region's current length,
  // the region auto-extends (rounded up to the nearest 1/4-bar) — NO
  // new region is created.
  // Returns the array of new note ids so the caller can update the
  // Piano Roll's selection state and chain Cmd+D presses.
  duplicateSelectedNotesInRegion: (regionId, selectedNoteIds) => {
    if (!Array.isArray(selectedNoteIds) || selectedNoteIds.length === 0) return [];
    const newIds = [];
    set((state) => {
      const region = state.regions.find((r) => r.id === regionId);
      if (!region) return {};
      const notes = region.notes || [];
      const selected = notes.filter((n) => selectedNoteIds.includes(n.id));
      if (selected.length === 0) return {};
      const minStep = Math.min(...selected.map((n) => n.step ?? 0));
      const maxEnd = Math.max(...selected.map((n) => (n.step ?? 0) + (n.duration || 1)));
      // v19.10 — Copy offset = full selection span (end - start). Copies
      // are placed IMMEDIATELY AFTER the last selected note's end so
      // there is never overlap with the originals, matching user-facing
      // "copy starts at bar 3" expectation for a bars 1-2 selection.
      const span = Math.max(1, maxEnd - minStep);
      // Per-pitch occupancy dodges any NON-selected same-pitch note the
      // copy would otherwise collide with. Selected notes are excluded
      // from the collision test because they're the source of the copy —
      // the copy is intentionally placed right past them.
      const selectedIdSet = new Set(selectedNoteIds);
      const byPitch = new Map();
      for (const n of notes) {
        if (selectedIdSet.has(n.id)) continue; // don't collide against ourselves
        if (!byPitch.has(n.pitch)) byPitch.set(n.pitch, []);
        byPitch.get(n.pitch).push(n);
      }
      const now = Date.now();
      const copies = selected.map((src, idx) => {
        let target = (src.step ?? 0) + span;
        const dur = src.duration || 1;
        const row = (byPitch.get(src.pitch) || []).slice().sort((a, b) => (a.step ?? 0) - (b.step ?? 0));
        for (const other of row) {
          const oStart = other.step ?? 0;
          const oEnd = oStart + (other.duration || 1);
          if (target + dur <= oStart) break;
          if (target < oEnd) target = oEnd;
        }
        const id = `n${now}_${Math.random().toString(36).slice(2, 5)}${idx}`;
        newIds.push(id);
        return { ...src, id, step: target };
      });
      // v12.7 — DO NOT auto-extend region.length. Notes can live past
      // the region's current length; they simply won't play until the
      // producer manually drags the region's right edge. Full manual
      // control per user spec.
      return {
        regions: state.regions.map((r) =>
          r.id === regionId ? { ...r, notes: [...notes, ...copies] } : r
        ),
      };
    });
    return newIds;
  },

  setSamplerEnv: (trackId, env) =>
    set((state) => ({
      tracks: state.tracks.map((t) =>
        t.id === trackId ? { ...t, samplerEnv: { ...(t.samplerEnv || {}), ...env } } : t
      ),
    })),

  // v13.1 — Bounce action REMOVED (major pivot to Stem Export). Any older
  // saved projects that referenced this action will now no-op; the
  // arranger UI no longer exposes a Bounce button. Legacy bounced tracks
  // are auto-purged by hydrateFromProject on load.

  // v12.5 — Reverse an audio clip in place. Sets a `reversed` flag on the
  // clip; AudioEngine reads this on playback and swaps the buffer sample
  // order. Toggling reverse again returns the clip to forward playback.
  reverseAudioClip: (trackId, clipId) =>
    set((state) => ({
      tracks: state.tracks.map((t) => {
        if (t.id !== trackId) return t;
        return {
          ...t,
          audioClips: (t.audioClips || []).map((c) =>
            c.id === clipId ? { ...c, reversed: !c.reversed } : c
          ),
        };
      }),
    })),

  setSamplerPadSample: (trackId, padId, sampleUrl, sampleName) =>
    set((state) => ({
      tracks: state.tracks.map((t) =>
        t.id === trackId
          ? { ...t, samplerPads: (t.samplerPads || []).map((p) => (p.id === padId ? { ...p, sampleUrl, sampleName } : p)) }
          : t
      ),
    })),
  setSamplerPadPitch: (trackId, padId, pitch) =>
    set((state) => ({
      tracks: state.tracks.map((t) =>
        t.id === trackId
          ? { ...t, samplerPads: (t.samplerPads || []).map((p) => (p.id === padId ? { ...p, pitch } : p)) }
          : t
      ),
    })),

  // Metronome
  metronomeOn: false,
  setMetronomeOn: (metronomeOn) => set({ metronomeOn }),

  // Audio clips on audio tracks
  addAudioClip: (trackId, clip) =>
    set((state) => ({
      tracks: state.tracks.map((t) =>
        t.id === trackId
          ? { ...t, audioClips: [...(t.audioClips || []), { id: `ac${Date.now()}_${Math.random().toString(36).slice(2,6)}`, length: 1, ...clip }] }
          : t
      ),
    })),
  updateAudioClip: (trackId, clipId, patch) =>
    set((state) => ({
      tracks: state.tracks.map((t) =>
        t.id === trackId
          ? { ...t, audioClips: (t.audioClips || []).map((c) => (c.id === clipId ? { ...c, ...patch } : c)) }
          : t
      ),
    })),
  removeAudioClip: (trackId, clipId) =>
    set((state) => ({
      tracks: state.tracks.map((t) =>
        t.id === trackId
          ? { ...t, audioClips: (t.audioClips || []).filter((c) => c.id !== clipId) }
          : t
      ),
      selectedIds: state.selectedIds.filter((x) => x !== clipId),
      selectedRegionId: state.selectedRegionId === clipId ? null : state.selectedRegionId,
    })),

  setEffect: (name, param, value) =>
    set((state) => ({
      effects: { ...state.effects, [name]: { ...state.effects[name], [param]: value } },
    })),

  saveProject: () => {
    const s = get();
    const project = {
      projectInitialized: s.projectInitialized,
      projectName: s.projectName, bpm: s.bpm,
      tracks: s.tracks.map((t) => ({ ...t, sampleUrl: null, audioClips: [] })),
      regions: s.regions, masterVolume: s.masterVolume,
      effects: s.effects, totalBars: s.totalBars,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(project));
    return project;
  },

  loadProject: () => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    try {
      const p = JSON.parse(raw);
      set({
        projectInitialized: true,
        projectName: p.projectName || "Untitled Project",
        bpm: p.bpm || 120,
        tracks: (p.tracks || []).map((t) => ({
          ...t,
          pattern: t.pattern || emptyPattern(),
          notes: t.notes || [],
          fxChain: t.fxChain || [],
          audioClips: [], // blob URLs don't survive reload
          sampleUrl: null,
          samplerEnv: t.samplerEnv || { attack: 0.005, decay: 0.1, sustain: 1, release: 0.2 },
        })),
        regions: p.regions || [],
        masterVolume: p.masterVolume ?? 0.8,
        effects: p.effects || defaultState.effects,
        totalBars: p.totalBars || 4,
      });
      return true;
    } catch { return false; }
  },

  resetProject: () => {
    // v11.3 — also blow away the local IndexedDB snapshot so the reset
    // button gives you a truly blank slate on next refresh.
    import("./localPersistence").then(({ clearLocalSnapshot }) => {
      clearLocalSnapshot().catch(() => {});
    });
    set({ ...defaultState });
  },
}));

// Expose store globally for e2e/debug tests + user console diagnostics.
// window.useDAWStore.getState() lets QA + power users script tracks/regions/notes.
if (typeof window !== "undefined") {
  try { window.useDAWStore = useDAWStore; } catch { /* noop */ }
}

